import { useEffect, useState } from "react";
import { Button, Card, Descriptions, message, Modal, Typography } from "antd";
import { useLocation, useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { getOrg } from "../../common/api/OrganizationClient";
import { handleError } from "../../common/ErrorHandling";
import { useUserState } from "../../common/UserContext";
import { Organization } from "../../types/OrganizationTypes";
import { impersonateOrgBot } from "../../common/api/UserClient";

const DEFAULT_TITLE = document.title;

export const OrganizationDetailView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const [loading, setLoading] = useState(false);
    const [org, setOrg] = useState<Organization>();
    const { refreshMe } = useUserState();

    const showModal = location.pathname.endsWith("/impersonate");

    const loadOrg = async () => {
        setLoading(true);
        try {
            if (!params.orgId) return;
            const orgId = Number(params.orgId);
            if (Number.isNaN(orgId)) return;
            const data = await getOrg(orgId);
            setOrg(data);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleImpersonate = async () => {
        if (!org?.id) return;
        try {
            await impersonateOrgBot(org.id);
            refreshMe();
            message.success("Organization impersonated successfully");
            navigate("..", { relative: "path", replace: true });
        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        loadOrg();
    }, [params.orgId]);

    useEffect(() => {
        if (!org?.name) return;
        document.title = `${DEFAULT_TITLE} - ${org?.name}`;

        return () => {
            document.title = DEFAULT_TITLE;
        };
    }, [org?.name]);

    return (
        <Card bordered={false} loading={loading}>
            {org?.id && (
                <Modal
                    open={showModal}
                    title="Impersonate"
                    onCancel={() => {
                        navigate("..", { relative: "path", replace: true });
                    }}
                    onOk={handleImpersonate}
                >
                    Are you sure you want to impersonate{" "}
                    <Typography.Text strong>{org?.name}</Typography.Text>?
                </Modal>
            )}
            <Descriptions
                title="Organization"
                extra={
                    <Link replace to="impersonate">
                        <Button size="small" type="primary">
                            Impersonate
                        </Button>
                    </Link>
                }
            >
                <Descriptions.Item label="Name">{org?.name}</Descriptions.Item>
                <Descriptions.Item label="Domain">{org?.domain}</Descriptions.Item>
            </Descriptions>
        </Card>
    );
};
